/** @file sheetPartWords: a sheet part in words: its name, layers drawn as one piece, its share of the height. */
import { partName } from '../partName';
import type { PlacedPart, RestPlacement } from './sheetTypes';

/** The part's quad box `[x0, y0, x1, y1]`. */
const boxOf = (p: PlacedPart): [number, number, number, number] => {
  const xs = p.quad.map((q) => q[0]);
  const ys = p.quad.map((q) => q[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
};

/**
 * `hand, right, on the screen's left, up to about 0.12 of the figure's height`; a node with several drawables reads
 * `head (hat, face, hair back, hair front as one piece)`. The height share is the part's tile against the figure's
 * box in the rest pose, so the model keeps the character's proportions.
 */
export function sheetPartWords(part: PlacedPart, placement: RestPlacement): string {
  const layers = placement.parts.filter((p) => p.node === part.node);
  const name = layers.length > 1 ? `${partName(part.node)} (${layers.map((p) => partName(p.drawable)).join(', ')} as one piece)` : partName(part.drawable);
  const boxes = placement.parts.map(boxOf);
  const top = Math.min(...boxes.map((b) => b[1]));
  const bottom = Math.max(...boxes.map((b) => b[3]));
  const left = Math.min(...boxes.map((b) => b[0]));
  const right = Math.max(...boxes.map((b) => b[2]));
  const [x0, y0, x1, y1] = boxOf(part);
  const centre = (x0 + x1) / 2;
  const third = (right - left) / 6;
  const side = centre < (left + right) / 2 - third ? ", on the screen's left" : centre > (left + right) / 2 + third ? ", on the screen's right" : '';
  const share = Math.round(((y1 - y0) / Math.max(1, bottom - top)) * 100) / 100;
  return `${name}${side}, up to about ${String(share)} of the figure's height`;
}
